<template>
  <div id="country_area_section" class="flex flex-col gap-6">
    <div id="country" class="form_col">
      <span class="secondary_color text-[13px]">Country</span>

      <a-select
        v-model:value="internalFormData.countryCode"
        class="w-full"
        placeholder="Select a country"
        :disabled="isInternationalPurchasingEnabled"
        @change="handleCountryChange"
      >
        <template #suffixIcon>
          <ThemeImage
            class="size-4 opacity-40"
            :icon="imageNames.BOTTOM_ARROW"
            alt="bottom_white_arrow"
          />
        </template>

        <a-select-option
          v-for="(country, index) in getCountryList(source)"
          :key="`${country.id}_country_${index}`"
          :value="country.id"
          :label="true"
        >
          <div class="flex flex-row items-center gap-2">
            <ThemeImage
              class="size-4"
              :icon="country.flag"
              alt="country_flag"
            />
            {{ country.name }} ({{ country.numberCode }})
          </div>
        </a-select-option>
      </a-select>
    </div>

    <div id="area_code" class="form_col" v-if="shouldShowAreaCode">
      <span class="secondary_color text-[13px]">Area Code</span>

      <a-select
        v-model:value="internalFormData.areaCode"
        class="w-full"
        placeholder="Select an area"
        show-search
        :filter-option="false"
        @search="filterAreaCode"
        @change="$emit('areaCodeChange')"
      >
        <template #suffixIcon
          ><ThemeImage
            class="size-4 opacity-40"
            :icon="imageNames.BOTTOM_ARROW"
            alt="bottom_white_arrow"
        /></template>

        <a-select-option
          v-for="(code, index) in areaCodesList"
          :key="`${code.area_code}_area_${index}`"
          :value="code.area_code"
        >
          {{ code.region }} ({{ code.area_code }})
        </a-select-option>
      </a-select>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, computed } from "vue";
import { getCountryList } from "@/src/constants/phoneNumberConstants.js";
import CallService from "@/src/api/CallService.js";
import { imageNames } from "@/src/constants/imageConstants.js";
import ThemeImage from "@/src/components/ThemeImage.vue";

const props = defineProps({
  internalFormData: {
    type: Object,
    required: true,
  },
  shouldShowAreaCode: {
    type: Boolean,
    required: true,
  },
  source: {
    type: String,
    default: "",
  },
});

const emit = defineEmits(["areaCodeChange", "countryChange"]);

const areaCodesList = ref(null);
const allAreaCodes = ref(null);
const searchText = ref("");
const previousCountryCode = ref(props.internalFormData.countryCode);

const isInternationalPurchasingEnabled = computed(() => {
  return !localStorage.getItem("purchasing_international_number");
});

const handleCountryChange = async (newCountryCode) => {
  const oldCountryCode = previousCountryCode.value;

  areaCodesList.value = null;
  allAreaCodes.value = null;
  searchText.value = "";

  if (props.shouldShowAreaCode) {
    await toGetAreaCodes();
  }

  previousCountryCode.value = newCountryCode;
  emit("countryChange", newCountryCode, oldCountryCode);
};

watch(
  () => props.internalFormData.countryCode,
  async (newCountryCode, oldCountryCode) => {
    if (oldCountryCode === newCountryCode) {
      return;
    }

    areaCodesList.value = null;
    allAreaCodes.value = null;
    searchText.value = "";

    if (props.shouldShowAreaCode) {
      await toGetAreaCodes();
    }
  },
);

const filterAreaCode = (search) => {
  try {
    searchText.value = search.toLowerCase();

    if (!searchText.value) {
      areaCodesList.value = allAreaCodes.value;
      return;
    }

    areaCodesList.value = allAreaCodes.value.filter((code) => {
      const regionMatch = code.region.toLowerCase().includes(searchText.value);
      const areaCodeMatch = code.area_code
        .toString()
        .includes(searchText.value);
      return regionMatch || areaCodeMatch;
    });
  } catch (error) {
    console.error("An error occurred while filtering area code: ", error);
  }
};

const toGetAreaCodes = async () => {
  try {
    const response = await CallService.getAreaCodes(
      props.internalFormData.countryCode,
    );
    if (response?.success) {
      allAreaCodes.value = response?.area_codes;
      areaCodesList.value = response?.area_codes;
    }
    return Promise.resolve();
  } catch (error) {
    console.error("An error occurred while getting area codes: ", error);
    return Promise.reject(error);
  }
};

onMounted(() => {
  if (props.shouldShowAreaCode) {
    toGetAreaCodes();
  }
});
</script>
